import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, ArrowLeft, Save, Send, Loader2, Info, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAMPOS_EDITABLES = [
  {
    key: 'email_previo_subject',
    label: 'Asunto (aviso 30 de agosto)',
    tipo: 'input',
    section: 'previo',
  },
  {
    key: 'email_previo_body',
    label: 'Cuerpo del email (aviso 30 de agosto)',
    tipo: 'textarea',
    rows: 8,
    section: 'previo',
  },
  {
    key: 'whatsapp_previo',
    label: 'Texto de WhatsApp (30 de agosto)',
    tipo: 'textarea',
    rows: 3,
    section: 'previo',
  },
  {
    key: 'email_apertura_subject',
    label: 'Asunto (apertura 1 de septiembre)',
    tipo: 'input',
    section: 'apertura',
  },
  {
    key: 'email_apertura_body',
    label: 'Cuerpo del email (apertura 1 de septiembre)',
    tipo: 'textarea',
    rows: 8,
    section: 'apertura',
  },
  {
    key: 'whatsapp_apertura',
    label: 'Texto de WhatsApp (1 de septiembre)',
    tipo: 'textarea',
    rows: 3,
    section: 'apertura',
  },
];

export default function AdminAuxiliaresRecordatoriosPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enviando, setEnviando] = useState(null); // 'previo' | 'apertura' | null

  const authHeaders = { Authorization: `Bearer ${token}` };
  const dirty = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(
          `${API_URL}/admin/auxiliares-justicia/config/recordatorios`,
          { headers: authHeaders }
        );
        const { _defaults, _fecha_actualizacion, _actualizado_por, ...rest } = r.data;
        setDefaults(_defaults);
        setConfig({
          ...rest,
          _fecha_actualizacion,
          _actualizado_por,
        });
      } catch (err) {
        toast.error('No se pudo cargar la configuración: ' + (err.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const actualizarCampo = (key, valor) => {
    dirty.current = true;
    setConfig(prev => ({ ...prev, [key]: valor }));
  };

  const guardar = async () => {
    setSaving(true);
    try {
      const payload = {
        activo: !!config.activo,
      };
      CAMPOS_EDITABLES.forEach(({ key }) => {
        payload[key] = config[key] ?? '';
      });
      const r = await axios.put(
        `${API_URL}/admin/auxiliares-justicia/config/recordatorios`,
        payload,
        { headers: authHeaders }
      );
      const { _defaults, ...rest } = r.data.config;
      setDefaults(_defaults);
      setConfig(rest);
      dirty.current = false;
      toast.success('Configuración guardada');
    } catch (err) {
      toast.error('Error al guardar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const enviarAhora = async (momento) => {
    const label = momento === 'previo' ? '30 de agosto (aviso previo)' : '1 de septiembre (apertura)';
    if (!window.confirm(
      `Vas a enviar el recordatorio "${label}" AHORA a todos los usuarios que alguna vez inscribieron.\n\n¿Continuar?`
    )) return;
    setEnviando(momento);
    try {
      const r = await axios.post(
        `${API_URL}/admin/auxiliares-justicia/config/recordatorios/enviar-ahora`,
        { momento },
        { headers: authHeaders, timeout: 180000 }
      );
      const s = r.data.resumen || {};
      toast.success(
        `Enviado a ${s.destinatarios} destinatarios · mails ok=${s.email_ok}/fail=${s.email_fail} · WA ok=${s.whatsapp_ok}/fail=${s.whatsapp_fail}`
      );
    } catch (err) {
      toast.error('Error al disparar el envío: ' + (err.response?.data?.detail || err.message));
    } finally {
      setEnviando(null);
    }
  };

  if (loading || !config) {
    return (
      <div className="p-6 flex items-center gap-2 text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando configuración...
      </div>
    );
  }

  const renderCampo = ({ key, label, tipo, rows }) => (
    <div key={key} className="space-y-1">
      <Label className="text-sm font-medium text-gray-700">{label}</Label>
      {tipo === 'textarea' ? (
        <Textarea
          rows={rows || 4}
          value={config[key] || ''}
          onChange={(e) => actualizarCampo(key, e.target.value)}
          className="font-mono text-sm"
          data-testid={`campo-${key}`}
        />
      ) : (
        <Input
          value={config[key] || ''}
          onChange={(e) => actualizarCampo(key, e.target.value)}
          data-testid={`campo-${key}`}
        />
      )}
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/auxiliares-justicia')} data-testid="btn-volver">
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver al Panel de Auxiliares
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-amber-600" />
          <span className="text-sm text-gray-500">Categoría: Recordatorios Anuales</span>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-2">
            <Bell className="w-6 h-6 text-amber-500" />
            Recordatorios Anuales de Auxiliares de Justicia
          </CardTitle>
          <CardDescription>
            Este texto se envía automáticamente todos los años por email y WhatsApp:
            el <b>30 de agosto</b> (aviso previo) y el <b>1 de septiembre</b> (apertura).
            Los destinatarios son todos los usuarios que alguna vez hayan hecho una
            inscripción de Auxiliares en el portal.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <Info className="w-4 h-4 text-amber-700 shrink-0" />
            <div className="text-xs text-amber-900">
              Placeholders disponibles: <code className="bg-amber-100 px-1 rounded">{'{nombre}'}</code>{' '}
              (apellido y nombres) y <code className="bg-amber-100 px-1 rounded">{'{anio_proximo}'}</code>{' '}
              (año de actuación = año actual + 1).
            </div>
          </div>

          <div className="mt-4 flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
            <Switch
              checked={!!config.activo}
              onCheckedChange={(v) => actualizarCampo('activo', v)}
              data-testid="switch-activo"
            />
            <Label className="text-sm font-medium">
              Campaña {config.activo ? 'ACTIVA' : 'DESACTIVADA'} — {config.activo
                ? 'los envíos automáticos del 30/08 y 01/09 corren normalmente.'
                : 'los envíos automáticos no se disparan.'}
            </Label>
          </div>

          {config._fecha_actualizacion && (
            <div className="mt-3 text-xs text-gray-500">
              Última actualización: {new Date(config._fecha_actualizacion).toLocaleString('es-AR')} · por{' '}
              <b>{config._actualizado_por || 'admin'}</b>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sección aviso previo (30/08) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg text-slate-800">
            Aviso previo — 30 de agosto (9:00 AR)
          </CardTitle>
          <CardDescription>Mensaje que llega la víspera de la apertura.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {CAMPOS_EDITABLES.filter(c => c.section === 'previo').map(renderCampo)}
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => enviarAhora('previo')}
              disabled={enviando === 'previo'}
              data-testid="btn-enviar-previo"
            >
              {enviando === 'previo' ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Enviando...</>
              ) : (
                <><Send className="w-4 h-4 mr-1" /> Enviar aviso previo ahora</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sección apertura (01/09) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg text-slate-800">
            Aviso de apertura — 1 de septiembre (9:00 AR)
          </CardTitle>
          <CardDescription>Mensaje del día en que abre la inscripción.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {CAMPOS_EDITABLES.filter(c => c.section === 'apertura').map(renderCampo)}
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => enviarAhora('apertura')}
              disabled={enviando === 'apertura'}
              data-testid="btn-enviar-apertura"
            >
              {enviando === 'apertura' ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Enviando...</>
              ) : (
                <><Send className="w-4 h-4 mr-1" /> Enviar aviso de apertura ahora</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sticky footer con guardar */}
      <div className="sticky bottom-0 bg-white border-t py-3 flex justify-between items-center">
        <div className="text-xs text-gray-500">
          <AlertCircle className="w-3 h-3 inline mr-1" />
          Recordá guardar los cambios antes de disparar un envío manual.
        </div>
        <Button
          onClick={guardar}
          disabled={saving}
          className="bg-slate-800 hover:bg-slate-900 text-white"
          data-testid="btn-guardar-config"
        >
          {saving ? (
            <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Guardando...</>
          ) : (
            <><Save className="w-4 h-4 mr-1" /> Guardar configuración</>
          )}
        </Button>
      </div>
    </div>
  );
}
